import { useState, useEffect, useRef, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X, MapPin } from "lucide-react";

const NAV_LINKS = [
  { label: "The Cabin", href: "#highlights" },
  { label: "Amenities", href: "#amenities" },
  { label: "Gallery", href: "/gallery" },
  { label: "Reviews", href: "#reviews" },
  { label: "Location", href: "#location" },
  { label: "Pontoon", href: "/pontoon" },
  { label: "Explore", href: "/explore" },
  { label: "Blog", href: "/blog" },
];

const SECTION_IDS = ["highlights", "amenities", "reviews", "location"] as const;

export default function GuestHeader({ actions }: { actions?: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [location] = useLocation();
  const isHome = location === "/";
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Track active section via IntersectionObserver on the home page
  useEffect(() => {
    if (!isHome) {
      setActiveSection(null);
      return;
    }
    observerRef.current?.disconnect();
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          // Use the topmost visible section
          const topmost = visible.reduce((a, b) =>
            a.boundingClientRect.top < b.boundingClientRect.top ? a : b
          );
          setActiveSection(topmost.target.id);
        }
      },
      { rootMargin: "-20% 0px -60% 0px", threshold: 0 }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    observerRef.current = observer;
    return () => observer.disconnect();
  }, [isHome]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  function resolveHref(href: string) {
    if (href.startsWith("#")) {
      return isHome ? href : `/${href}`;
    }
    return href;
  }

  function handleNavClick(href: string) {
    setMenuOpen(false);
    if (isHome && href.startsWith("#")) {
      const id = href.slice(1);
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  }

  function isActive(link: (typeof NAV_LINKS)[number]) {
    if (link.href.startsWith("#")) {
      return isHome && activeSection === link.href.slice(1);
    }
    return location === link.href;
  }

  return (
    <header
      className={`sticky top-0 z-50 bg-background/95 backdrop-blur-md transition-shadow duration-200 ${
        scrolled ? "shadow-sm border-b border-border/60" : "border-b border-border/40"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link href="/" className="flex items-center gap-3 min-w-0 group" data-testid="link-home-logo">
          <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center shrink-0 shadow-sm group-hover:shadow-md transition-shadow duration-150">
            <img src="/cabin-mark.svg" alt="" className="w-[18px] h-[18px] brightness-0 invert" />
          </div>
          <div className="min-w-0">
            <span className="font-serif font-bold text-[13px] sm:text-[15px] text-foreground leading-tight block tracking-tight whitespace-nowrap truncate">
              Legendary Log Cabin
            </span>
            <span className="text-[11px] text-muted-foreground hidden sm:flex items-center gap-1 whitespace-nowrap">
              <MapPin className="w-2.5 h-2.5 shrink-0" />Lake Miltona, MN
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7" data-testid="nav-desktop">
          {NAV_LINKS.map((link) => {
            const active = isActive(link);
            return (
              <a
                key={link.label}
                href={resolveHref(link.href)}
                onClick={(e) => {
                  if (isHome && link.href.startsWith("#")) {
                    e.preventDefault();
                    handleNavClick(link.href);
                  }
                }}
                className={`relative text-sm font-medium whitespace-nowrap transition-colors duration-150 py-1 group ${
                  active
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`link-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {link.label}
                <span
                  className={`absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-primary transition-all duration-150 ${
                    active ? "opacity-100" : "opacity-0 group-hover:opacity-40"
                  }`}
                />
              </a>
            );
          })}
        </nav>

        <div className="flex items-center gap-1 shrink-0" data-testid="header-actions">
          {actions}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors duration-150 text-foreground"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            data-testid="button-hamburger-menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          menuOpen ? "max-h-[32rem] opacity-100" : "max-h-0 opacity-0"
        }`}
        data-testid="mobile-menu"
      >
        <nav className="border-t border-border/50 bg-background/98 px-4 py-3 space-y-0.5">
          {NAV_LINKS.map((link) => {
            const active = isActive(link);
            return (
              <a
                key={link.label}
                href={resolveHref(link.href)}
                onClick={(e) => {
                  if (isHome && link.href.startsWith("#")) {
                    e.preventDefault();
                  }
                  handleNavClick(link.href);
                }}
                className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                  active
                    ? "bg-primary/10 text-primary"
                    : "text-foreground hover:bg-muted/70"
                }`}
                data-testid={`mobile-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {active && <span className="w-1.5 h-1.5 rounded-full bg-primary mr-2 shrink-0" />}
                {link.label}
              </a>
            );
          })}
          <a
            href="/admin"
            onClick={() => setMenuOpen(false)}
            className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-muted/70 transition-colors duration-150"
            data-testid="mobile-link-host-login"
          >
            Host Login
          </a>
        </nav>
      </div>
    </header>
  );
}
